import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Button, Segmented, InputNumber, Space, Typography, Tooltip, theme } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  UndoOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  WarningOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatMoneyCompact } from '../utils/formatters';
import { fiscalMonthKey } from '../utils';
import type { SalesPlanData } from './SalesPlan';

const MONTH_KEYS_SHORT = [
  'idea_calc.forecast_amazon.month_jan', 'idea_calc.forecast_amazon.month_feb',
  'idea_calc.forecast_amazon.month_mar', 'idea_calc.forecast_amazon.month_apr',
  'idea_calc.forecast_amazon.month_may', 'idea_calc.forecast_amazon.month_jun',
  'idea_calc.forecast_amazon.month_jul', 'idea_calc.forecast_amazon.month_aug',
  'idea_calc.forecast_amazon.month_sep', 'idea_calc.forecast_amazon.month_oct',
  'idea_calc.forecast_amazon.month_nov', 'idea_calc.forecast_amazon.month_dec',
];

type ForecastType = 'pessimistic' | 'realistic' | 'optimistic';

interface MonthData {
  sales: number;
  price: number;
}

interface CalculatorData {
  purchasePrice: number;
  shippingCostPerKg: number;
  unitWeightKg: number;
  shippingPrice: number;
  storageFee: number;
  sellingPrice: number;
  referralFee: number;
  fbaFee: number;
  otherFees: number;
  ppcCost: number;
  conversionRate: number;
  ppcSalesPercentage: number;
  returnRate: number;
  resellableReturns: number;
  monthlySales: number;
  batchQuantity: number;
}

export interface ForecastSummaryData {
  cogsPerUnit: number;
  sellingPrice: number;
  positiveMonthPL: number;
  positiveMonthCF: number;
  minCashFlow: number;
  monthlyData: {
    month: string;
    revenue: number;
    profit: number;
    pl: number;
    cumPL: number;
    cashFlow: number;
    cumCF: number;
  }[];
  yearlyData: {
    year: string;
    revenue: number;
    revenueGrowth: number;
    pl: number;
    plGrowth: number;
    cashFlow: number;
    cfGrowth: number;
    adSpend: number;
    fixedCosts: number;
    profit: number;
    profitWithoutAds: number;
    avgMargin: number;
    avgROI: number;
  }[];
  totalData: {
    revenue: number;
    pl: number;
    cashFlow: number;
    adSpend: number;
    fixedCosts: number;
    profit: number;
    profitWithoutAds: number;
    avgMargin: number;
    avgROI: number;
  };
}

interface ForecastAmazonUSProps {
  calculatorData?: CalculatorData;
  salesPlanData?: SalesPlanData;
  launchBudget?: number;
  onSummaryChange?: (summary: ForecastSummaryData) => void;
}

const defaultCalcData: CalculatorData = {
  purchasePrice: 4.5,
  shippingCostPerKg: 1.3,
  unitWeightKg: 0.5,
  shippingPrice: 0.65,
  storageFee: 0.1,
  sellingPrice: 35.59,
  referralFee: 15,
  fbaFee: 8.36,
  otherFees: 0,
  ppcCost: 0.9,
  conversionRate: 19,
  ppcSalesPercentage: 70,
  returnRate: 4.5,
  resellableReturns: 75,
  monthlySales: 0,
  batchQuantity: 400,
};

const defaultSalesPlan: SalesPlanData = {
  salesGrowth: [
    { year: 2025, annualGrowth: 40, monthlyGrowth: 2.8 },
    { year: 2026, annualGrowth: 35, monthlyGrowth: 2.5 },
    { year: 2027, annualGrowth: 30, monthlyGrowth: 2.2 },
    { year: 2028, annualGrowth: 25, monthlyGrowth: 1.9 },
    { year: 2029, annualGrowth: 20, monthlyGrowth: 1.5 },
  ],
  monthlyGrowth2025: [
    { month: 1, monthName: fiscalMonthKey(1), growth: 0 },
    { month: 2, monthName: fiscalMonthKey(2), growth: 5 },
    { month: 3, monthName: fiscalMonthKey(3), growth: 8 },
    { month: 4, monthName: fiscalMonthKey(4), growth: 3 },
    { month: 5, monthName: fiscalMonthKey(5), growth: 4 },
    { month: 6, monthName: fiscalMonthKey(6), growth: 6 },
    { month: 7, monthName: fiscalMonthKey(7), growth: 2 },
    { month: 8, monthName: fiscalMonthKey(8), growth: 3 },
    { month: 9, monthName: fiscalMonthKey(9), growth: 4 },
    { month: 10, monthName: fiscalMonthKey(10), growth: 3 },
    { month: 11, monthName: fiscalMonthKey(11), growth: 2 },
    { month: 12, monthName: fiscalMonthKey(12), growth: 4 },
  ],
  tacos: [
    { month: 1, tacos: 55 },
    { month: 2, tacos: 45 },
    { month: 3, tacos: 35 },
    { month: 4, tacos: 25 },
    { month: 5, tacos: 20 },
  ],
  targetTacos: 17,
};

function generateMonthLabels(t: (key: string) => string): string[] {
  const start = new Date();
  start.setDate(start.getDate() + 30);
  let mi = start.getMonth();
  let year = start.getFullYear() % 100;
  const months: string[] = [];
  for (let i = 0; i < 60; i++) {
    months.push(`${t(MONTH_KEYS_SHORT[mi])} ${year}`);
    mi++;
    if (mi === 12) { mi = 0; year++; }
  }
  return months;
}

const ForecastAmazonUS: React.FC<ForecastAmazonUSProps> = ({
  calculatorData = defaultCalcData,
  salesPlanData = defaultSalesPlan,
  launchBudget: launchBudgetProp,
  onSummaryChange,
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [forecastType, setForecastType] = useState<ForecastType>('realistic');
  const [scrollOffset, setScrollOffset] = useState(0);

  // Constants (previously in settings modal)
  const LAUNCH_BUDGET = launchBudgetProp ?? 15000;
  const MONTHLY_EXPENSES = 500;
  const DEPOSIT_PERCENT = 30;
  const REMAINING_PAYMENT_DELAY = 0;
  const PRODUCTION_WEEKS = 4;
  const DELIVERY_WEEKS = 2;

  // Base sales: use monthlySales from calculator if provided, with multipliers for forecast types
  const BASE_SALES_DEFAULT: Record<ForecastType, number> = { pessimistic: 35, realistic: 50, optimistic: 75 };
  const BASE_SALES_MULTIPLIER: Record<ForecastType, number> = { pessimistic: 0.7, realistic: 1.0, optimistic: 1.5 };

  const getBaseSales = useCallback((ft: ForecastType): number => {
    if (calculatorData.monthlySales && calculatorData.monthlySales > 0) {
      return Math.round(calculatorData.monthlySales * BASE_SALES_MULTIPLIER[ft]);
    }
    return BASE_SALES_DEFAULT[ft];
  }, [calculatorData.monthlySales]);

  const [manuallyEdited, setManuallyEdited] = useState<boolean[]>(Array(60).fill(false));
  const manuallyEditedRef = useRef(manuallyEdited);
  manuallyEditedRef.current = manuallyEdited;

  // Track original edit: {value, type} so we can scale proportionally on type switch
  const editBaseRef = useRef<{ value: number; type: ForecastType } | null>(null);

  const allMonthLabels = useMemo(() => generateMonthLabels(t), [t]);

  const calculateSalesForMonth = useCallback(
    (monthIndex: number, ft: ForecastType): number => {
      const base = getBaseSales(ft);
      if (monthIndex === 0) return base;

      // First 12 months: growth is relative to base (month 0), not previous month
      if (monthIndex <= 12) {
        const md = salesPlanData.monthlyGrowth2025.find((m) => m.month === monthIndex);
        const rate = md ? md.growth / 100 : 0;
        return Math.round(base * (1 + rate));
      }

      // Months 13+: compound from month 12 value using yearly growth rates
      const md12 = salesPlanData.monthlyGrowth2025.find((m) => m.month === 12);
      const rate12 = md12 ? md12.growth / 100 : 0;
      let sales = base * (1 + rate12);
      for (let i = 13; i <= monthIndex; i++) {
        const cy = 2025 + Math.floor((i - 1) / 12);
        const yd = salesPlanData.salesGrowth.find((s) => s.year === cy);
        const rate = yd ? yd.monthlyGrowth / 100 : 0.02;
        sales *= 1 + rate;
      }
      return Math.round(sales);
    },
    [salesPlanData, getBaseSales],
  );

  const [monthsData, setMonthsData] = useState<MonthData[]>(() =>
    Array.from({ length: 60 }, (_, i) => ({
      sales: calculateSalesForMonth(i, 'realistic'),
      price: calculatorData.sellingPrice,
    })),
  );

  const unitCogs = calculatorData.purchasePrice + calculatorData.shippingPrice;
  const batchQty = calculatorData.batchQuantity;
  const firstOrderAmount = batchQty * unitCogs;
  const unitReferralFee = (monthsData[0]?.price || calculatorData.sellingPrice) * (calculatorData.referralFee / 100);
  const unitFbaFee = calculatorData.fbaFee;
  const unitOtherFees = calculatorData.otherFees;
  const unitStorageFee = calculatorData.storageFee;

  // Precomputed data for all 60 months — single O(n) forward pass
  const precomputed = useMemo(() => {
    // Inventory simulation
    let inv = batchQty;
    const orders: { orderMonth: number; deliveryMonth: number; quantity: number }[] = [];
    const leadTimeMonths = Math.ceil((PRODUCTION_WEEKS + DELIVERY_WEEKS) / 4);
    const invSnapshots: { current: number; order?: number }[] = [];

    for (let i = 0; i < 60; i++) {
      const sales = monthsData[i]?.sales || 0;
      inv -= sales;
      for (const o of orders) {
        if (o.deliveryMonth === i) inv += o.quantity;
      }

      if (i < 59) {
        const forecastPeriod = leadTimeMonths + 1;
        let futureSales = 0;
        for (let j = i + 1; j <= Math.min(i + forecastPeriod, 59); j++) {
          futureSales += monthsData[j]?.sales || monthsData[i]?.sales || 0;
        }
        let incomingStock = 0;
        for (const o of orders) {
          if (o.deliveryMonth > i && o.deliveryMonth <= i + forecastPeriod) incomingStock += o.quantity;
        }

        if (inv + incomingStock < futureSales) {
          let orderQty = futureSales - (inv + incomingStock);
          for (let j = i + forecastPeriod + 1; j <= Math.min(i + forecastPeriod + 3, 59); j++) {
            orderQty += monthsData[j]?.sales || monthsData[i]?.sales || 0;
          }
          orderQty = Math.max(orderQty, batchQty);
          orders.push({ orderMonth: i, deliveryMonth: i + leadTimeMonths, quantity: orderQty });
        }
      }

      const currentOrder = orders.find((o) => o.orderMonth === i);
      invSnapshots.push({ current: Math.max(inv, 0), order: currentOrder?.quantity });
    }

    // Forward pass for all financial metrics
    const months: {
      revenue: number; cumRevenue: number; allFees: number;
      ppcSalesPercent: number; adSpend: number; returnCosts: number;
      invCurrent: number; invOrder: number | undefined;
      monthlyProfit: number; profitWithoutAds: number; pl: number; cumPL: number;
      cfMonthly: number; cfCum: number; payments: number;
      margin: number; marginWithoutAds: number; monthlyROI: number; cumROI: number; cumInvValue: number;
    }[] = [];

    let cumPL = 0, cumCF = 0, cumRevenue = 0, cumInvValue = firstOrderAmount;

    for (let mi = 0; mi < 60; mi++) {
      const sales = monthsData[mi]?.sales || 0;
      const price = monthsData[mi]?.price || 0;
      const returnRate = calculatorData.returnRate / 100;
      // Revenue accounts for returns - returned units = refunded, no revenue
      const revenue = sales * price * (1 - returnRate);
      cumRevenue += revenue;

      // PPC sales percentage: use tacos schedule (decreasing over time) or calculator's ppcSalesPercentage as fallback
      const ppcSalesPercent = mi < salesPlanData.tacos.length
        ? salesPlanData.tacos[mi].tacos
        : (calculatorData.ppcSalesPercentage || salesPlanData.targetTacos);

      // Calculate ad spend: (sales through PPC / conversion rate) * CPC
      const ppcSales = sales * (ppcSalesPercent / 100);
      const conversionRate = calculatorData.conversionRate > 0 ? calculatorData.conversionRate / 100 : 0.1;
      const adSpend = conversionRate > 0 ? (ppcSales / conversionRate) * calculatorData.ppcCost : 0;

      const allFees = sales * (unitReferralFee + unitFbaFee + unitOtherFees + unitStorageFee);

      // Return costs:
      // - Lost units (non-resellable): full COGS loss
      // - Resellable units: return processing fee (~15% of price)
      // - Amazon keeps 20% of referral fee on returns
      const resellableRate = calculatorData.resellableReturns / 100;
      const lostRate = 1 - resellableRate;
      const returned = sales * returnRate;

      const lostUnitsCost = returned * lostRate * unitCogs;
      const returnProcessingCost = returned * resellableRate * price * 0.15;
      const referralFeeLoss = returned * unitReferralFee * 0.2;
      const returnCosts = lostUnitsCost + returnProcessingCost + referralFeeLoss;

      const monthlyProfit = revenue - allFees - adSpend - sales * unitCogs - returnCosts;
      const profitWithoutAds = revenue - allFees - sales * unitCogs - returnCosts; // Profit without ad spend
      let pl = monthlyProfit - MONTHLY_EXPENSES;
      if (mi === 0) pl -= LAUNCH_BUDGET;
      cumPL += pl;

      // Cash flow payments
      let payments = 0;
      if (mi === 0) {
        payments = firstOrderAmount * (DEPOSIT_PERCENT / 100);
      } else if (mi === REMAINING_PAYMENT_DELAY) {
        payments = firstOrderAmount * ((100 - DEPOSIT_PERCENT) / 100);
      }

      const invData = invSnapshots[mi];
      if (invData.order) {
        payments += invData.order * unitCogs * (DEPOSIT_PERCENT / 100);
        cumInvValue += invData.order * unitCogs;
      }
      if (mi >= REMAINING_PAYMENT_DELAY && mi > 0) {
        const prevInv = invSnapshots[mi - REMAINING_PAYMENT_DELAY];
        if (prevInv.order) {
          payments += prevInv.order * unitCogs * ((100 - DEPOSIT_PERCENT) / 100);
        }
      }

      const cfMonthly = pl - payments;
      cumCF += cfMonthly;

      const margin = revenue > 0 ? (monthlyProfit / revenue) * 100 : 0;
      const marginWithoutAds = revenue > 0 ? (profitWithoutAds / revenue) * 100 : 0;
      const totalInv = cumInvValue + LAUNCH_BUDGET;
      const cumROI = totalInv > 0 ? (cumPL / totalInv) * 100 : 0;
      const monthlyCost = sales * unitCogs + allFees + adSpend + returnCosts + MONTHLY_EXPENSES;
      const monthlyROI = monthlyCost > 0 ? (monthlyProfit / monthlyCost) * 100 : 0;

      months.push({
        revenue, cumRevenue, allFees, ppcSalesPercent, adSpend, returnCosts,
        invCurrent: invData.current, invOrder: invData.order,
        monthlyProfit, profitWithoutAds, pl, cumPL, cfMonthly, cfCum: cumCF, payments,
        margin, marginWithoutAds, monthlyROI, cumROI, cumInvValue,
      });
    }

    return months;
  }, [monthsData, salesPlanData, calculatorData, unitCogs, unitReferralFee, unitFbaFee, unitOtherFees, unitStorageFee, batchQty, firstOrderAmount]);

  const updateMonthData = (monthIndex: number, field: keyof MonthData, value: number) => {
    if (field === 'sales') {
      const newEdited = [...manuallyEditedRef.current];
      newEdited[monthIndex] = true;
      manuallyEditedRef.current = newEdited;
      setManuallyEdited(newEdited);
      if (monthIndex === 0) {
        editBaseRef.current = { value, type: forecastType };
      }
    }
    setMonthsData((prev) => {
      const newData = [...prev];
      newData[monthIndex] = { ...newData[monthIndex], [field]: value };
      if (field === 'sales') {
        const edited = manuallyEditedRef.current;
        const base = monthIndex === 0 ? value : newData[0].sales;
        for (let i = monthIndex + 1; i < newData.length; i++) {
          if (edited[i]) break;
          if (i <= 12) {
            const md = salesPlanData.monthlyGrowth2025.find((m) => m.month === i);
            const rate = md ? md.growth / 100 : 0;
            newData[i] = { ...newData[i], sales: Math.round(base * (1 + rate)) };
          } else {
            const cy = 2025 + Math.floor((i - 1) / 12);
            const yd = salesPlanData.salesGrowth.find((s) => s.year === cy);
            const rate = yd ? yd.monthlyGrowth / 100 : 0.02;
            newData[i] = { ...newData[i], sales: Math.round(newData[i - 1].sales * (1 + rate)) };
          }
        }
      }
      return newData;
    });
  };

  const resetToAutoCalculation = () => {
    const cleared = Array(60).fill(false);
    manuallyEditedRef.current = cleared;
    setManuallyEdited(cleared);
    editBaseRef.current = null;
    setMonthsData((prev) =>
      prev.map((m, i) => ({ ...m, sales: calculateSalesForMonth(i, forecastType) })),
    );
  };

  // Recalc on type switch — scale edited base proportionally, recalculate the rest
  useEffect(() => {
    const edited = manuallyEditedRef.current;
    const eb = editBaseRef.current;
    setMonthsData((prev) => {
      const newData = [...prev];
      // Month 0: if edited, scale proportionally; else use forecast type default
      let base: number;
      if (edited[0] && eb) {
        base = Math.round(eb.value * getBaseSales(forecastType) / getBaseSales(eb.type));
      } else {
        base = getBaseSales(forecastType);
      }
      newData[0] = { ...newData[0], sales: base, price: calculatorData.sellingPrice };
      // Months 1-12: grow from base
      for (let i = 1; i <= 12; i++) {
        newData[i] = { ...newData[i], price: calculatorData.sellingPrice };
        if (edited[i]) continue;
        const md = salesPlanData.monthlyGrowth2025.find((m) => m.month === i);
        const rate = md ? md.growth / 100 : 0;
        newData[i] = { ...newData[i], sales: Math.round(base * (1 + rate)) };
      }
      // Months 13+: compound from month 12
      for (let i = 13; i < newData.length; i++) {
        newData[i] = { ...newData[i], price: calculatorData.sellingPrice };
        if (edited[i]) continue;
        const cy = 2025 + Math.floor((i - 1) / 12);
        const yd = salesPlanData.salesGrowth.find((s) => s.year === cy);
        const rate = yd ? yd.monthlyGrowth / 100 : 0.02;
        newData[i] = { ...newData[i], sales: Math.round(newData[i - 1].sales * (1 + rate)) };
      }
      return newData;
    });
  }, [forecastType, calculatorData.sellingPrice, salesPlanData, getBaseSales]);

  // Summary callback — uses precomputed O(1) lookups instead of O(n²) recalculations
  useEffect(() => {
    if (!onSummaryChange) return;
    const years = [t('idea_calc.forecast_amazon.year_2025_partial'), '2026', '2027', '2028', '2029'];
    let prevRev = 0, prevPL = 0, prevCF = 0;
    const yearlyData: ForecastSummaryData['yearlyData'] = [];

    for (let yi = 0; yi < years.length; yi++) {
      const start = yi === 0 ? 0 : (yi - 1) * 12 + 6;
      const end = yi === 0 ? 6 : yi * 12 + 6;
      let yRev = 0, yPL = 0, yCF = 0, yAd = 0, yFC = 0, yProfit = 0, yProfitNoAds = 0;
      for (let m = start; m < Math.min(end, 60); m++) {
        const p = precomputed[m];
        yRev += p.revenue;
        yPL += p.pl;
        yCF += p.cfMonthly;
        yAd += p.adSpend;
        yFC += MONTHLY_EXPENSES;
        yProfit += p.monthlyProfit;
        yProfitNoAds += p.profitWithoutAds;
      }
      if (yi === 0) { yPL -= LAUNCH_BUDGET; yFC += LAUNCH_BUDGET; }
      const revG = prevRev > 0 ? ((yRev - prevRev) / prevRev) * 100 : 0;
      const plG = prevPL !== 0 ? ((yPL - prevPL) / Math.abs(prevPL)) * 100 : 0;
      const cfG = prevCF !== 0 ? ((yCF - prevCF) / Math.abs(prevCF)) * 100 : 0;
      const avgM = yRev > 0 ? (yProfit / yRev) * 100 : 0;
      const lastMonth = Math.min(end - 1, 59);
      const totalInv = precomputed[lastMonth].cumInvValue + LAUNCH_BUDGET;
      const avgROI = totalInv > 0 ? (yPL / totalInv) * 100 : 0;

      yearlyData.push({
        year: years[yi], revenue: Math.round(yRev), revenueGrowth: Math.round(revG),
        pl: Math.round(yPL), plGrowth: Math.round(plG), cashFlow: Math.round(yCF), cfGrowth: Math.round(cfG),
        adSpend: Math.round(yAd), fixedCosts: Math.round(yFC), profit: Math.round(yProfit),
        profitWithoutAds: Math.round(yProfitNoAds),
        avgMargin: Math.round(avgM * 10) / 10, avgROI: Math.round(avgROI * 10) / 10,
      });

      if (yi === 0) { prevRev = yRev * 2; prevPL = yPL * 2; prevCF = yCF * 2; }
      else { prevRev = yRev; prevPL = yPL; prevCF = yCF; }
    }

    let posMonthPL = 0, posMonthCF = 0, minCF = 0;
    for (let i = 0; i < 60; i++) {
      if (!posMonthPL && precomputed[i].pl > 0) posMonthPL = i + 1;
      if (!posMonthCF && precomputed[i].cfCum > 0) posMonthCF = i + 1;
      if (precomputed[i].cfCum < minCF) minCF = precomputed[i].cfCum;
    }

    // Build monthly data for chart
    const monthlyData = precomputed.map((p, i) => ({
      month: allMonthLabels[i],
      revenue: Math.round(p.revenue),
      profit: Math.round(p.monthlyProfit),
      pl: Math.round(p.pl),
      cumPL: Math.round(p.cumPL),
      cashFlow: Math.round(p.cfMonthly),
      cumCF: Math.round(p.cfCum),
    }));

    const tRev = yearlyData.reduce((s, y) => s + y.revenue, 0);
    const tPL = yearlyData.reduce((s, y) => s + y.pl, 0);
    const tCF = yearlyData.reduce((s, y) => s + y.cashFlow, 0);
    const tAd = yearlyData.reduce((s, y) => s + y.adSpend, 0);
    const tFC = yearlyData.reduce((s, y) => s + y.fixedCosts, 0);
    const tProfit = yearlyData.reduce((s, y) => s + y.profit, 0);
    const tProfitNoAds = yearlyData.reduce((s, y) => s + y.profitWithoutAds, 0);
    const tInv = precomputed[59].cumInvValue + LAUNCH_BUDGET;

    onSummaryChange({
      cogsPerUnit: unitCogs, sellingPrice: calculatorData.sellingPrice,
      positiveMonthPL: posMonthPL, positiveMonthCF: posMonthCF, minCashFlow: Math.round(minCF),
      monthlyData,
      yearlyData,
      totalData: {
        revenue: Math.round(tRev), pl: Math.round(tPL), cashFlow: Math.round(tCF),
        adSpend: Math.round(tAd), fixedCosts: Math.round(tFC), profit: Math.round(tProfit),
        profitWithoutAds: Math.round(tProfitNoAds),
        avgMargin: tRev > 0 ? Math.round((tProfit / tRev) * 1000) / 10 : 0,
        avgROI: tInv > 0 ? Math.round((tPL / tInv) * 1000) / 10 : 0,
      },
    });
  }, [precomputed, onSummaryChange, unitCogs, calculatorData.sellingPrice, allMonthLabels]);

  // Visible columns slice
  const visibleCount = 12;
  const visibleStart = scrollOffset;
  const visibleEnd = Math.min(scrollOffset + visibleCount, 60);

  // Row definitions for the table
  type RowDef = {
    label: string;
    sublabel?: string;
    render: (mi: number) => React.ReactNode;
    editable?: boolean;
    bgColor?: string;
  };

  const coloredMoney = (v: number) => (
    <span style={{ color: v >= 0 ? token.colorSuccess : token.colorError, fontWeight: 500 }}>
      {formatMoneyCompact(v)}
    </span>
  );

  const rows: RowDef[] = [
    {
      label: t('idea_calc.forecast_amazon.row_sales'),
      editable: true,
      render: (mi) => {
        if (mi < 10) {
          return (
            <InputNumber
              size="small"
              controls={false}
              min={0}
              step={1}
              value={monthsData[mi]?.sales}
              onChange={(v) => updateMonthData(mi, 'sales', v ?? 0)}
              style={{
                width: 70,
                textAlign: 'center',
                fontWeight: manuallyEdited[mi] ? 600 : 400,
                color: manuallyEdited[mi] ? '#d46b08' : undefined,
              }}
            />
          );
        }
        return (
          <span style={{ color: token.colorTextTertiary }}>
            <LockOutlined style={{ fontSize: 9, marginRight: 2 }} />
            {monthsData[mi]?.sales}
          </span>
        );
      },
      bgColor: '#e6f7ff',
    },
    {
      label: t('idea_calc.forecast_amazon.row_price'),
      render: (mi) => <span>${monthsData[mi]?.price.toFixed(2)}</span>,
    },
    {
      label: t('idea_calc.forecast_amazon.row_revenue'),
      sublabel: t('idea_calc.forecast_amazon.row_revenue_sub'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div>{formatMoneyCompact(p.revenue)}</div>
            <div style={{ fontSize: 11, color: token.colorTextTertiary, fontStyle: 'italic' }}>
              {'\u03A3'} {formatMoneyCompact(p.cumRevenue)}
            </div>
          </div>
        );
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_fees'),
      render: (mi) => <span>{formatMoneyCompact(precomputed[mi].allFees)}</span>,
    },
    {
      label: t('idea_calc.forecast_amazon.row_ppc_sales'),
      render: (mi) => <span>{precomputed[mi].ppcSalesPercent.toFixed(0)}%</span>,
    },
    {
      label: t('idea_calc.forecast_amazon.row_ads'),
      render: (mi) => <span style={{ color: token.colorWarning }}>{formatMoneyCompact(precomputed[mi].adSpend)}</span>,
    },
    {
      label: t('idea_calc.forecast_amazon.row_inventory'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div>{p.invCurrent}</div>
            {p.invOrder && (
              <span style={{
                background: token.colorPrimaryBg,
                color: token.colorPrimary,
                borderRadius: 4,
                padding: '1px 4px',
                fontSize: 11,
                fontWeight: 600,
              }}>
                +{p.invOrder}
              </span>
            )}
          </div>
        );
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_profit'),
      sublabel: t('idea_calc.forecast_amazon.row_profit_sub'),
      render: (mi) => coloredMoney(precomputed[mi].monthlyProfit),
    },
    {
      label: t('idea_calc.forecast_amazon.row_profit_no_ads'),
      sublabel: t('idea_calc.forecast_amazon.row_profit_no_ads_sub'),
      render: (mi) => (
        <span style={{ color: token.colorSuccess, fontWeight: 500 }}>
          {formatMoneyCompact(precomputed[mi].profitWithoutAds)}
        </span>
      ),
      bgColor: '#f6ffed',
    },
    {
      label: t('idea_calc.forecast_amazon.row_pl'),
      sublabel: t('idea_calc.forecast_amazon.row_pl_sub'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div>{coloredMoney(p.pl)}</div>
            <div style={{ fontSize: 11, color: token.colorTextTertiary, fontStyle: 'italic' }}>
              {'\u03A3'} {formatMoneyCompact(p.cumPL)}
            </div>
          </div>
        );
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_cash_flow'),
      sublabel: t('idea_calc.forecast_amazon.row_cash_flow_sub'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div>{coloredMoney(p.cfMonthly)}</div>
            <div style={{ fontSize: 11, color: token.colorTextTertiary, fontStyle: 'italic' }}>
              {'\u03A3'} {formatMoneyCompact(p.cfCum)}
            </div>
          </div>
        );
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_margin'),
      render: (mi) => {
        const m = precomputed[mi].margin;
        return <span style={{ color: m >= 0 ? token.colorSuccess : token.colorError }}>{m.toFixed(1)}%</span>;
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_roi'),
      sublabel: t('idea_calc.forecast_amazon.row_roi_sub'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div style={{ color: p.monthlyROI >= 0 ? token.colorSuccess : token.colorError }}>{p.monthlyROI.toFixed(1)}%</div>
            <div style={{ fontSize: 11, color: token.colorTextTertiary, fontStyle: 'italic' }}>
              {'\u03A3'} {p.cumROI.toFixed(1)}%
            </div>
          </div>
        );
      },
    },
  ];

  const thStyle: React.CSSProperties = {
    padding: '8px 6px',
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 600,
    borderRight: '1px solid #f0f0f0',
    whiteSpace: 'nowrap',
    minWidth: 85,
  };

  const tdStyle: React.CSSProperties = {
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: 12,
    borderRight: '1px solid #f0f0f0',
    borderBottom: '1px solid #fafafa',
  };

  const stickyTd: React.CSSProperties = {
    position: 'sticky',
    left: 0,
    background: '#fff',
    zIndex: 2,
    padding: '6px 12px',
    borderRight: '1px solid #e8e8e8',
    borderBottom: '1px solid #fafafa',
    minWidth: 160,
  };

  return (
    <Card
      title={t('idea_calc.forecast_amazon.title')}
      variant="outlined"
    >
      {/* Forecast type selector + navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Segmented
          size="small"
          value={forecastType}
          onChange={(v) => setForecastType(v as ForecastType)}
          options={[
            { label: <><WarningOutlined /> {t('idea_calc.forecast_amazon.scenario_pessimistic')}</>, value: 'pessimistic' },
            { label: <><CheckCircleOutlined /> {t('idea_calc.forecast_amazon.scenario_realistic')}</>, value: 'realistic' },
            { label: <><RocketOutlined /> {t('idea_calc.forecast_amazon.scenario_optimistic')}</>, value: 'optimistic' },
          ]}
        />
        <Space>
          <Button
            size="small"
            type="link"
            icon={<UndoOutlined />}
            onClick={resetToAutoCalculation}
          >
            {t('idea_calc.forecast_amazon.reset_button')}
          </Button>
          <Button
            size="small"
            icon={<LeftOutlined />}
            disabled={scrollOffset === 0}
            onClick={() => setScrollOffset(Math.max(0, scrollOffset - 12))}
          />
          <Tooltip title={t('idea_calc.forecast_amazon.scroll_tooltip')}>
            <Typography.Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
              {scrollOffset + 1}–{visibleEnd} / 60
            </Typography.Text>
          </Tooltip>
          <Button
            size="small"
            icon={<RightOutlined />}
            disabled={visibleEnd >= 60}
            onClick={() => setScrollOffset(Math.min(48, scrollOffset + 12))}
          />
        </Space>
      </div>

      {/* Forecast table */}
      <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={{ ...thStyle, textAlign: 'left', position: 'sticky', left: 0, background: '#fafafa', zIndex: 3, minWidth: 160 }}>
                {t('idea_calc.forecast_amazon.col_metric')}
              </th>
              {allMonthLabels.slice(visibleStart, visibleEnd).map((label, idx) => (
                <th key={visibleStart + idx} style={thStyle}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td style={stickyTd}>
                  <div style={{ fontWeight: 500, color: '#595959' }}>{row.label}</div>
                  {row.sublabel && (
                    <div style={{ fontSize: 11, color: token.colorTextTertiary }}>{row.sublabel}</div>
                  )}
                </td>
                {Array.from({ length: visibleEnd - visibleStart }, (_, idx) => {
                  const mi = visibleStart + idx;
                  return (
                    <td
                      key={mi}
                      style={{
                        ...tdStyle,
                        background: row.editable && mi < 10
                          ? (manuallyEdited[mi] ? '#fffbe6' : '#e6f7ff')
                          : undefined,
                      }}
                    >
                      {row.render(mi)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </Card>
  );
};

export default React.memo(ForecastAmazonUS);
